import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { Token } from '../../interfaces/token';
import { ApiConfigService } from '../../ssr-services/api-config/api-config.service';
import { CacheService } from '../../ssr-services/cache/cache.service';
import { SubscriberService } from '../../ssr-services/subscriber/subscriber.service';

export interface SignupRequest {
  message: string;
  signupRequestId: string;
  otpNextGenerationAt: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class RegistrationService {
  protected cacheService = inject(CacheService);

  protected apiConfig = inject(ApiConfigService);

  protected http = inject(HttpClient);

  protected subscriberService = inject(SubscriberService);

  /**
   * Generates signup request which returns temporary signup request id for usage against email verification
   * @param data
   * @returns
   */
  public generateSignUpRequest(data: {
    email: string;
    password: string;
    referrerReferralCode?: string;
  }): Observable<{ signupRequestId: string; otpNextGenerationAt: number | null }> {
    const cacheContent = this.cacheService.cachedContent();
    const subscriberLanguage = this.subscriberService.subscriberLanguage();
    const subscriberCurrency = this.subscriberService.subscriberCurrency();

    return this.http
      .post<{ signupRequestId: string; otpNextGenerationAt: number }>(
        this.apiConfig.url('subscribers/signup'),
        {
          ...data,
          customerId: cacheContent?.cache.customerId,
          language: subscriberLanguage.code,
          currency: subscriberCurrency.currency,
        }
      )
      .pipe(
        map((result) => ({
          signupRequestId: result.signupRequestId,
          otpNextGenerationAt: result.otpNextGenerationAt || null,
        }))
      );
  }

  /**
   * Verifies the otp with signup request
   * @param signUpRequestId
   * @param otp
   * @returns
   */
  public verifySignUpOtp(
    signupRequestId: string,
    otp: string
  ): Observable<{ accountStatus: boolean; tokenResponse: Token; message: string }> {
    return this.http.post<{ accountStatus: boolean; tokenResponse: Token; message: string }>(
      this.apiConfig.url('subscribers/verifyOTP'),
      {
        signupRequestId,
        otp,
      }
    );
  }

  /**
   * Regenerates a new OTP
   * @param email
   * @returns
   */
  public regenerateSignUpOtp(email: string): Observable<{ message: string }> {
    const cacheContent = this.cacheService.cachedContent();

    return this.http.post<{ message: string }>(this.apiConfig.url('subscribers/resendOTP'), {
      email,
      customerId: cacheContent?.cache.customerId,
    });
  }
}
